import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  DatePicker,
  Form,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormSelect,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'shards-react';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faSearch, faSpinner, faTasks, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import PageTitle from '../components/common/PageTitle';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import useGridOptionsInventoryJob from '../hooks/grid/gridOptions/useGridOptionsInventoryJob';
import commonColDef from '../data/grid/commonColDef';
import useColdefInventoryJob from '../hooks/grid/coldef/useColdefInventoryJob';
import ActionsInventoryJobRenderer from '../components/cellRenderers/ActionsInventoryJobRenderer';
import UserNameFromEmailCellRenderer from '../components/cellRenderers/UserNameFromEmailCellRenderer';
import IsActiveRenderer from '../components/cellRenderers/IsActiveRenderer';
import { useTranslation } from 'react-i18next';
import useLocales from '../hooks/useLocales';
import useUserRoles from '../hooks/useUserRoles';
import { RootState, useDispatch, useSelector } from '../redux/store';
import {
  resetMessage,
  addRow,
  setDeleteFlag,
  setAbortFlag,
  abortJob,
  getInventoryJobs,
  getUserCheckCount,
  searchInventoryJobs,
  editInventoryJob,
  editAction,
  closeDialog,
} from '../redux/slices/inventoryJob';
import { getUsers } from '../redux/slices/user';
import moment from 'moment';
import { DATE_COMPARISON_OPTIONS } from '../constants';
import type { ColDef, GridApi, ColumnApi, GridReadyEvent } from 'ag-grid-community';
import type { DateComparisonOperator } from '../types/inventoryJob';

const ManageInventoryJob = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { hasError, isLoading, serverMessage, editMode, editData, deleteFlag, abortFlag, isDialogOpen, dialogTitle, dialogInfo, inventoryJobRowData, userCheckCount } = useSelector(
    (state: RootState) => state.inventoryJob,
  );
  const { users } = useSelector((state: RootState) => state.user);
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsInventoryJob();
  const customColDef: ColDef = {
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const coldefInventoryJob = useColdefInventoryJob(t);
  const frameworkComponents = {
    actionsRenderer: ActionsInventoryJobRenderer,
    isActiveRenderer: IsActiveRenderer,
    userNameFromEmailCellRenderer: UserNameFromEmailCellRenderer,
  };
  const [dateComparisonOption, setDateComparisonOption] = useState<DateComparisonOperator>('gte');
  const [dateInput, setDateInput] = useState<Date>(moment().toDate());
  const searchJobReady = dateComparisonOption.length && dateInput;
  const [filteringText, setFilteringText] = useState<string>('');
  const { isMaintenanceadmin } = useUserRoles();

  /**
   * 確認用
   */
  // useEffect(() => {
  //   console.log(hasDateInputError);
  // }, [hasDateInputError]);

  useEffect(() => {
    // 初回データ取得
    dispatch(getUsers({}));
    dispatch(getInventoryJobs());
    dispatch(getUserCheckCount());
  }, []);

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  /**
   * メッセージを消す
   */
  const dismiss = (): void => {
    dispatch(resetMessage());
  };

  /**
   * 日付入力
   */
  const handleDateInput = (selectedDate: Date): void => {
    setDateInput(selectedDate);
  };

  /**
   * 日付比較選択
   */
  const handleSelectDateComparison = (event): void => {
    // console.log(event.target.value);
    setDateComparisonOption(event.target.value);
  };

  /**
   * ジョブ検索
   */
  const handleSearchJob = (): void => {
    dispatch(resetMessage());
    const formattedDate: string = moment(dateInput).format('YYYY-MM-DD');
    dispatch(searchInventoryJobs(formattedDate, dateComparisonOption));
  };

  /**
   * 検索リセット
   */
  const handleResetSearch = (): void => {
    dispatch(getInventoryJobs());
  };

  /**
   * テキストフィルタリング
   */
  const handleSearchTextInput = (event): void => {
    const searchText: string = event.target.value;
    setFilteringText(searchText);
    if (gridApi) {
      gridApi.setQuickFilter(searchText);
    }
  };
  const handleClearFilteringText = (): void => {
    setFilteringText('');
    if (gridApi) {
      gridApi.setQuickFilter('');
    }
  };

  /**
   * 行の追加
   */
  const handleAddRow = () => {
    dispatch(addRow());
  };

  /**
   * モーダルの項目名変換
   */
  const replaceRowKey = (keyName: string): string => {
    if (keyName === 'firstMailDate') {
      return t('inventory.initialContactDate');
    } else if (keyName === 'secondMailDate') {
      return t('inventory.secondContactDate');
    } else if (keyName === 'lastDeleteDate') {
      return t('inventory.deletionDate');
    } else if (keyName === 'isActived') {
      return t('isValid.valid');
    } else {
      return keyName;
    }
  };

  /**
   * 行の編集
   */
  const handleEditRow = (event, key: string): void => {
    // console.log(event.target.checked);
    let editValue: string | boolean;
    if (key === 'isActived') {
      editValue = event.target.checked;
    } else {
      editValue = moment(event).toISOString();
    }
    dispatch(editInventoryJob(key, editValue, editData));
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleDialogCancel = (): void => {
    dispatch(closeDialog());
    dispatch(setDeleteFlag(false));
    dispatch(setAbortFlag(false));
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleDialogApprove = (): void => {
    dispatch(closeDialog());
    // console.log(editMode, editData)
    dispatch(editAction(editMode, editData));
    dispatch(setDeleteFlag(false));
  };

  /**
   * モーダルで中止ボタンを押した時
   */
  const handleAbortJob = (): void => {
    dispatch(closeDialog());
    // console.log(editMode, editData)
    dispatch(abortJob(editData));
    dispatch(setAbortFlag(false));
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    // console.log(editMode, editData);
    dispatch(setDeleteFlag(!deleteFlag));
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleAbort = (): void => {
    // console.log(editMode, editData);
    dispatch(setAbortFlag(!abortFlag));
  };

  return (
    <Container fluid className="main-content-container px-4">
      <Row id="pageTitle" noGutters className="page-header py-4">
        <PageTitle sm="4" title={t('inventory.job')} subtitle="Inventory Job" className="text-sm-left" />
      </Row>

      <div id="success" className="pb-1">
        <Alert id="successServer" theme="success" dismissible={dismiss} open={!hasError && serverMessage !== undefined}>
          <span>{serverMessage}</span>
        </Alert>
      </div>

      <div id="alerts" className="pb-1">
        <Alert id="alertServer" theme="danger" dismissible={dismiss} open={hasError}>
          <span>{serverMessage}</span>
        </Alert>
      </div>

      <Row className="mb-2" form>
        <Col>
          <InputGroup>
            <InputGroupAddon type="prepend">
              <InputGroupText>{t('filter.initialContactDate')}</InputGroupText>
            </InputGroupAddon>
            <div style={{ width: '8rem' }}>
              <DatePicker selected={dateInput} onChange={handleDateInput} placeholderText="Select Date" dropdownMode="select" dateFormat="yyyy-MM-dd" className="text-center rounded-0" />
            </div>
            <FormSelect onChange={handleSelectDateComparison} defaultValue={'gte'}>
              {DATE_COMPARISON_OPTIONS.map((item) => {
                return (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                );
              })}
            </FormSelect>
            <InputGroupAddon type="append">
              <Button size="sm" disabled={!searchJobReady} onClick={handleSearchJob}>
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
                &nbsp;&nbsp;{t('filter.search')}
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </Col>
        <Col md="2">
          <InputGroup>
            <Button theme="light" onClick={handleResetSearch}>
              <FontAwesomeIcon icon={faTimesCircle} />
              &nbsp;{t('filter.reset')}
            </Button>
          </InputGroup>
        </Col>
        {/* <Col>
        </Col> */}
        <Col md="2">
          <InputGroup seamless={true}>
            <FormInput id="grid-search-text" onChange={handleSearchTextInput} placeholder={t('filter.default')} value={filteringText} />
            <InputGroupAddon type="append">
              <InputGroupText>
                <FontAwesomeIcon icon={faTimesCircle} onClick={handleClearFilteringText} style={{ cursor: 'pointer' }} />
              </InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </Col>
      </Row>

      <div className="area-exec">
        <Form>
          <FormGroup>
            <div className="ag-theme-balham" style={{ height: 'calc(100vh - 20em)' }}>
              <AgGridReact
                defaultColDef={defaultColDef}
                columnDefs={coldefInventoryJob}
                gridOptions={gridOptions}
                rowData={inventoryJobRowData}
                onGridReady={onGridReady}
                frameworkComponents={frameworkComponents}
                // filterValueGetterで使うデータ
                context={{
                  currentLang,
                  // reduxにあるものはコピーを渡す
                  users: users?.map((user) => structuredClone(user)),
                }}
              />
            </div>
          </FormGroup>
        </Form>
      </div>

      {isMaintenanceadmin && (
        <Row className="mb-2" form>
          <Col></Col>
          <Col md="2" className="d-flex justify-content-end align-items-end">
            <Button size="sm" onClick={handleAddRow}>
              <FontAwesomeIcon icon={faPlusCircle} />
              &nbsp;&nbsp;{t('inventory.add')}
            </Button>
          </Col>
        </Row>
      )}

      <Modal
        id="deleteConfirmationModal"
        open={isDialogOpen && editMode === 'delete'}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={'xs'}
        fullWidth={true}
        centered={true}
        toggle={handleDialogCancel}>
        <ModalHeader>
          <div
          // className={classes.avatar}
          >
            <FontAwesomeIcon icon={faTasks} className="mr-2" />
            {/* {dialogTitle} */}
            {t('inventory.dialog.titleDelete')}
          </div>
        </ModalHeader>
        <ModalBody>
          {!dialogInfo.length ? (
            <></>
          ) : (
            <div
            // className={classes.table}
            >
              <Table aria-label="job info table">
                <tbody>
                  {dialogInfo.map((row, idx) => (
                    <tr key={'row' + idx}>
                      <th scope="row">{replaceRowKey(row.key)}</th>
                      <td>
                        {
                          // isActivedフィールドでないならDatePicker
                          <DatePicker
                            selected={row.value && moment(String(row.value)).toDate()}
                            onChange={(event) => handleEditRow(event, row.key)}
                            placeholderText="Select Date"
                            dropdownMode="select"
                            dateFormat="yyyy-MM-dd"
                            minDate={new Date()}
                            className="text-center"
                            disabled={true}
                          />
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="px-0">
          <Button theme="light" onClick={handleDialogCancel}>
            {t('dialog.cancel')}
          </Button>
          <Button theme="danger" disabled={!deleteFlag || !isMaintenanceadmin} onClick={handleDialogApprove}>
            {t('dialog.delete')}
          </Button>
          <FormCheckbox inline checked={deleteFlag} onChange={handleToggleDelete}>
            {t('dialog.validDelete')}
          </FormCheckbox>
        </ModalFooter>
      </Modal>

      <Modal
        id="editConfirmationModal"
        open={isDialogOpen && editMode !== 'delete'}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={'xs'}
        fullWidth={true}
        centered={true}
        toggle={handleDialogCancel}>
        <ModalHeader>
          <div
          // className={classes.avatar}
          >
            <FontAwesomeIcon icon={faTasks} className="mr-2" />
            {t('inventory.dialog.titleUpdate')}
          </div>
        </ModalHeader>
        <ModalBody>
          {!dialogInfo.length ? (
            <></>
          ) : (
            <div
            // className={classes.table}
            >
              <Table aria-label="job info table">
                <tbody>
                  {dialogInfo.map((row, idx) => (
                    <tr key={'row' + idx}>
                      <th scope="row">{replaceRowKey(row.key)}</th>
                      <td>
                        {
                          // isActivedフィールドでないならDatePicker
                          <DatePicker
                            selected={row.value && moment(String(row.value)).toDate()}
                            onChange={(event) => handleEditRow(event, row.key)}
                            placeholderText="Select Date"
                            dropdownMode="select"
                            dateFormat="yyyy-MM-dd"
                            minDate={new Date()}
                            className="text-center"
                            disabled={moment(row.value).isSameOrBefore(new Date(), 'day')}
                          />
                        }
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <th>{t('inventory.targetNumber')}</th>
                    <td>{userCheckCount}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {editData?.jobStatus === '1' && (
            <>
              <Button theme="danger" disabled={!abortFlag} onClick={handleAbortJob}>
                {t('inventory.dialog.stop')}
              </Button>
              <FormCheckbox inline checked={abortFlag} onChange={handleToggleAbort}>
                {t('inventory.dialog.validStop')}
              </FormCheckbox>
            </>
          )}
          <Button theme="light" onClick={handleDialogCancel}>
            {t('dialog.cancel')}
          </Button>
          <Button theme="primary" disabled={!isMaintenanceadmin} onClick={handleDialogApprove}>
            {t('dialog.update')}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default ManageInventoryJob;
